import {
  collection,
  doc,
  getDoc,
  getDocFromServer,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  orderBy,
  query,
  where,
  Timestamp,
  writeBatch,
  arrayUnion,
  arrayRemove,
  deleteField,
  WriteBatch,
} from "firebase/firestore";
import { db } from "../firebase/config";
import {
  Session,
  ActivityMeasurement,
  SessionTemplate,
  TrainingLog,
} from "../types/post";
import { ActivityVariable } from "../types/activity";
import { addLogIDToDayPost, removeLogIDFromDayPost } from "./dayPostService";
import { getLinkedSessionIDs } from "../utils/sessionLinks";
import { activityService } from "./activityService";
import { organisationService } from "./organisationService";
import { getUserPublicData } from "./userService";
import { UserPrivateData } from "../types/user";
import { get } from "http";

/**
 * Fetch coach display details (first name + surname) by trying private data first,
 * falling back to public username.
 */
async function getCoachDisplayInfo(coachId: string): Promise<{
  name: string;
  surname?: string;
  imageUrl?: string;
  imageUrlTiny?: string;
} | null> {
  try {
    const publicData = await getUserPublicData(coachId);
    if (!publicData) return null;

    let firstName: string | undefined;
    let surname: string | undefined;

    // Try to fetch private data for real name
    try {
      const privateDoc = await getDoc(
        doc(db, "users", coachId, "private", "data"),
      );
      if (privateDoc.exists()) {
        const priv = privateDoc.data() as UserPrivateData;
        if (priv.name) firstName = priv.name;
        if (priv.surname) surname = priv.surname;
      }
    } catch {
      // Permission denied — fall back to username
    }

    return {
      name: firstName || publicData.username || "Coach",
      surname,
      imageUrl: publicData.imageUrl,
      imageUrlTiny: publicData.imageUrlTiny,
    };
  } catch (error) {
    console.error("Error fetching coach display info:", error);
    return null;
  }
}

/**
 * Recursively sanitize data for Firestore:
 * - Remove any keys with undefined values
 * - For arrays, sanitize each element
 * - For ActivityVariable.measurement, convert undefined → null
 */
function sanitizeForFirestore<T>(data: T): T {
  if (Array.isArray(data)) {
    // Sanitize each element and drop any undefined elements
    const arr = (data as unknown as any[]).map((item) =>
      sanitizeForFirestore(item),
    );
    return arr.filter((el) => el !== undefined) as unknown as T;
  }
  if (data && typeof data === "object") {
    // Preserve Firestore Timestamp or Date-like objects
    // Firestore Timestamp exposes a toDate() function; leave it untouched
    if ((data as any)?.toDate && typeof (data as any).toDate === "function") {
      return data;
    }
    if (data instanceof Date) {
      return data;
    }
    const result: any = {};
    Object.keys(data as any).forEach((key) => {
      const value = (data as any)[key];
      if (value === undefined) {
        // Skip undefined keys entirely
        return;
      }
      // Special handling for ActivityVariable objects
      if (
        value &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        Object.prototype.hasOwnProperty.call(value, "name") &&
        Object.prototype.hasOwnProperty.call(value, "measurement")
      ) {
        const v = { ...value };
        if ((v as any).measurement === undefined) {
          (v as any).measurement = null;
        }
        result[key] = sanitizeForFirestore(v);
        return;
      }
      result[key] = sanitizeForFirestore(value);
    });
    return result;
  }
  return data;
}

function toJsDate(value: any): Date | undefined {
  if (!value) return undefined;
  if (typeof value?.toDate === "function") {
    try {
      return value.toDate();
    } catch {
      // fallthrough
    }
  }
  if (value instanceof Date) return value;
  if (typeof value === "string" || typeof value === "number") {
    const d = new Date(value);
    return isNaN(d.getTime()) ? undefined : d;
  }
  return undefined;
}

export class SessionService {
  /**
   * Create a new session
   */
  static async createSession(
    userId: string,
    sessionData: {
      ActivityMeasurementList?: ActivityMeasurement[];
      activityBlocks?: Session["activityBlocks"];
      plannedDate: string;
      name?: string;
      trainingTypeID?: string;
      /** @deprecated Legacy composite ID kept for old callers; use trainingTypeID */
      sessionTypeID?: string;
      periodisation?: string;
      description: string;
      status?: "planned" | "executing" | "complete";
      startTime?: string;
      endTime?: string;
      /** True only for a session created by pressing Start, not planned first. */
      startedAdHoc?: boolean;
      linkedTrainingLogID?: string;
      programLinkage?: Session["programLinkage"];
      /** Optional link to an event (Events.md §1.2). */
      eventId?: string;
    },
  ): Promise<string> {
    if (!userId) {
      throw new Error("User ID is required");
    }

    if (!sessionData.plannedDate) {
      throw new Error("Planned date is required");
    }

    try {
      // Build Firestore document data, excluding undefined values
      const firestoreData: any = {
        ActivityMeasurementList: sessionData.ActivityMeasurementList || [],
        plannedDate: sessionData.plannedDate,
        description: sessionData.description.trim() || "",
        status: sessionData.status || "planned",
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      // Only include optional fields if they have truthy values (not undefined, null, or empty string)
      if (
        sessionData.name !== undefined &&
        sessionData.name !== null &&
        typeof sessionData.name === "string" &&
        sessionData.name.trim() !== ""
      ) {
        firestoreData.name = sessionData.name.trim();
      }
      if (
        typeof sessionData.trainingTypeID === "string" &&
        sessionData.trainingTypeID.trim() !== ""
      ) {
        firestoreData.trainingTypeID = sessionData.trainingTypeID.trim();
      }
      if (
        sessionData.sessionTypeID !== undefined &&
        sessionData.sessionTypeID !== null &&
        typeof sessionData.sessionTypeID === "string" &&
        sessionData.sessionTypeID.trim() !== ""
      ) {
        firestoreData.sessionTypeID = sessionData.sessionTypeID.trim();
      }
      if (
        sessionData.periodisation !== undefined &&
        sessionData.periodisation !== null &&
        typeof sessionData.periodisation === "string" &&
        sessionData.periodisation.trim() !== ""
      ) {
        firestoreData.periodisation = sessionData.periodisation.trim();
      }
      if (
        sessionData.startTime !== undefined &&
        sessionData.startTime !== null &&
        typeof sessionData.startTime === "string" &&
        sessionData.startTime.trim() !== ""
      ) {
        firestoreData.startTime = sessionData.startTime.trim();
      }
      if (
        sessionData.endTime !== undefined &&
        sessionData.endTime !== null &&
        typeof sessionData.endTime === "string" &&
        sessionData.endTime.trim() !== ""
      ) {
        firestoreData.endTime = sessionData.endTime.trim();
      }
      if (sessionData.startedAdHoc === true) {
        firestoreData.startedAdHoc = true;
      }
      if (
        typeof sessionData.eventId === "string" &&
        sessionData.eventId.trim() !== ""
      ) {
        firestoreData.eventId = sessionData.eventId.trim();
      }
      if (sessionData.programLinkage) {
        firestoreData.programLinkage = sanitizeForFirestore(
          sessionData.programLinkage,
        );
      }
      if (
        Array.isArray(sessionData.activityBlocks) &&
        sessionData.activityBlocks.length > 0
      ) {
        firestoreData.activityBlocks = sanitizeForFirestore(
          sessionData.activityBlocks,
        );
      }

      // Debug: Log the data being sent (check for undefined values)
      // console.log("Creating session with data:", firestoreData);
      // console.log(
      //   "Has name field?",
      //   "name" in firestoreData,
      //   "Value:",
      //   firestoreData.name,
      // );
      // console.log(
      //   "Has sessionTypeID field?",
      //   "sessionTypeID" in firestoreData,
      //   "Value:",
      //   firestoreData.sessionTypeID,
      // );

      const docRef = await addDoc(
        collection(db, "sessions", userId, "sessions"),
        firestoreData,
      );

      // Add session ID to the day post for the planned date
      const dateID = sessionData.plannedDate.replace(/-/g, ""); // Convert YYYY-MM-DD to YYYYMMDD
      await addLogIDToDayPost(userId, dateID, docRef.id, "sessionIDs");

      // Auto-link to training log if provided (writes both the log's
      // linkedSessionIDs array and the legacy scalar).
      if (sessionData.linkedTrainingLogID) {
        try {
          await this.linkSessionToLog(
            userId,
            docRef.id,
            sessionData.linkedTrainingLogID,
            dateID,
          );
        } catch (linkError) {
          console.error("Error auto-linking training log:", linkError);
          // Don't throw - session creation succeeded, linking is optional
        }
      }

      return docRef.id;
    } catch (error) {
      console.error("Error creating session:", error);
      throw new Error("Failed to create session");
    }
  }

  /**
   * Get a session by ID
   * Checks regular sessions first, then proposed sessions if not found
   */
  static async getSession(
    userId: string,
    sessionId: string,
  ): Promise<Session | null> {
    if (!userId) {
      throw new Error("User ID is required");
    }

    if (!sessionId) {
      throw new Error("Session ID is required");
    }

    try {
      // First, try to get from regular sessions
      const docRef = doc(db, "sessions", userId, "sessions", sessionId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();

        // Fetch coach details if session was issued by a coach
        let coachData = null;
        if (data.coachIssued && data.coachID) {
          try {
            const coachInfo = await getCoachDisplayInfo(data.coachID);
            if (coachInfo) {
              coachData = coachInfo;
            }
          } catch (error) {
            console.error("Error fetching coach details:", error);
          }
        }

        return {
          id: docSnap.id,
          ...data,
          createdAt: toJsDate(data.createdAt),
          updatedAt: toJsDate(data.updatedAt),
          ...(coachData && { coach: coachData }),
        } as Session;
      }

      return null;
    } catch (error) {
      console.error("Error getting session:", error);
      throw new Error("Failed to get session");
    }
  }

  /**
   * Get a session by ID from server (bypasses cache, forces fresh data)
   * Use this when you need guaranteed up-to-date data, especially after writes
   */
  static async getSessionFromServer(
    userId: string,
    sessionId: string,
  ): Promise<Session | null> {
    if (!userId) {
      throw new Error("User ID is required");
    }

    if (!sessionId) {
      throw new Error("Session ID is required");
    }

    try {
      const docRef = doc(db, "sessions", userId, "sessions", sessionId);
      // Use getDocFromServer to force fetch from server, bypassing cache
      const docSnap = await getDocFromServer(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: toJsDate(data.createdAt),
          updatedAt: toJsDate(data.updatedAt),
        } as Session;
      }

      return null;
    } catch (error) {
      console.error("Error getting session from server:", error);
      throw new Error("Failed to get session from server");
    }
  }

  /**
   * Get multiple sessions by IDs
   */
  static async getSessions(
    userId: string,
    sessionIds: string[],
  ): Promise<(Session & { id: string })[]> {
    if (!userId) {
      throw new Error("User ID is required");
    }

    if (!sessionIds || sessionIds.length === 0) {
      return [];
    }

    try {
      const sessions = await Promise.all(
        sessionIds.map(async (sessionId) => {
          const session = await this.getSession(userId, sessionId);
          return session ? { ...session, id: sessionId } : null;
        }),
      );

      return sessions.filter(
        (session): session is Session & { id: string } => session !== null,
      );
    } catch (error) {
      console.error("Error getting sessions:", error);
      throw new Error("Failed to get sessions");
    }
  }

  /**
   * Get all sessions for a user
   */
  static async getAllSessions(
    userId: string,
  ): Promise<(Session & { id: string })[]> {
    if (!userId) {
      throw new Error("User ID is required");
    }

    try {
      const sessionsRef = collection(db, "sessions", userId, "sessions");
      const q = query(sessionsRef, orderBy("plannedDate", "desc"));
      const snapshot = await getDocs(q);

      const sessions = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: toJsDate(data.createdAt),
          updatedAt: toJsDate(data.updatedAt),
        } as Session & { id: string };
      });

      // Fetch coach details for sessions that were issued by a coach
      const sessionsWithCoachDetails = await Promise.all(
        sessions.map(async (session) => {
          try {
            // Check if session was issued by a coach
            const coachId = session.coachID;
            if (coachId) {
              const coachInfo = await getCoachDisplayInfo(coachId);
              if (coachInfo) {
                return {
                  ...session,
                  coach: coachInfo,
                };
              }
            }
            return session;
          } catch (error) {
            console.error("Error fetching coach details for session:", error);
            // Return session without coach details if fetch fails
            return session;
          }
        }),
      );

      return sessionsWithCoachDetails;
    } catch (error) {
      console.error("Error getting all sessions:", error);
      throw new Error("Failed to get sessions");
    }
  }

  /**
   * Accept a proposed session - converts it to a regular session with coach metadata
   */
  static async acceptProposedSession(
    userId: string,
    proposedSessionId: string,
    coachId: string,
  ): Promise<string> {
    if (!userId) {
      throw new Error("User ID is required");
    }

    if (!proposedSessionId) {
      throw new Error("Proposed session ID is required");
    }

    if (!coachId) {
      throw new Error("Coach ID is required");
    }

    try {
      const sessionRef = doc(
        db,
        "sessions",
        userId,
        "sessions",
        proposedSessionId,
      );

      await updateDoc(sessionRef, {
        status: "planned",
        coachIssued: true,
        coachID: coachId,
        updatedAt: Timestamp.now(),
      });

      // console.log(`Accepted proposed session ${proposedSessionId}`);
      return proposedSessionId;
    } catch (error) {
      // console.error("Error accepting proposed session:", error);
      throw new Error("Failed to accept proposed session");
    }
  }

  /**
   * Reject a proposed session - changes status to rejected, recording the
   * athlete's reason so the coach can see why it was declined.
   */
  static async rejectProposedSession(
    userId: string,
    proposedSessionId: string,
    reason: string,
  ): Promise<void> {
    if (!userId) {
      throw new Error("User ID is required");
    }

    if (!proposedSessionId) {
      throw new Error("Proposed session ID is required");
    }

    try {
      const proposedSessionRef = doc(
        db,
        "sessions",
        userId,
        "sessions",
        proposedSessionId,
      );
      const trimmedReason = reason?.trim();
      await updateDoc(proposedSessionRef, {
        status: "rejected",
        ...(trimmedReason ? { rejectionReason: trimmedReason } : {}),
        updatedAt: Timestamp.now(),
      });
      // console.log(`Rejected proposed session ${proposedSessionId}`);
    } catch (error) {
      // console.error("Error rejecting proposed session:", error);
      throw new Error("Failed to reject proposed session");
    }
  }

  /**
   * Get all proposed sessions for a user (created by their coach)
   */
  static async getProposedSessions(
    userId: string,
  ): Promise<(Session & { id: string })[]> {
    if (!userId) {
      throw new Error("User ID is required");
    }

    try {
      const proposedSessionsRef = collection(
        db,
        "sessions",
        userId,
        "sessions",
      );
      const q = query(
        proposedSessionsRef,
        where("status", "==", "proposed"),
        orderBy("plannedDate", "desc"),
      );
      const snapshot = await getDocs(q);
      // console.log(
      //   `Fetched ${snapshot.size} proposed sessions for user ${userId}`,
      // );

      const sessions = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: toJsDate(data.createdAt),
          updatedAt: toJsDate(data.updatedAt),
        } as Session & { id: string };
      });

      // Fetch coach details for each session
      const sessionsWithCoachDetails = await Promise.all(
        sessions.map(async (session) => {
          try {
            const coachId = session.coachID;
            if (coachId) {
              const coachInfo = await getCoachDisplayInfo(coachId);
              if (coachInfo) {
                return {
                  ...session,
                  coach: coachInfo,
                };
              }
            }
            return session;
          } catch (error) {
            console.warn("Error fetching coach details for session:", error);
            return session;
          }
        }),
      );

      return sessionsWithCoachDetails;
    } catch (error) {
      console.error("Error getting proposed sessions:", error);
      throw new Error("Failed to get proposed sessions");
    }
  }

  /**
   * Get sessions for a date range (inclusive)
   * @param startDate YYYY-MM-DD
   * @param endDate YYYY-MM-DD
   */
  static async getSessionsForDateRange(
    userId: string,
    startDate: string,
    endDate: string,
  ): Promise<(Session & { id: string })[]> {
    if (!userId) throw new Error("User ID is required");
    if (!startDate || !endDate)
      throw new Error("Start and end dates are required");

    try {
      const sessionsRef = collection(db, "sessions", userId, "sessions");
      const q = query(
        sessionsRef,
        where("plannedDate", ">=", startDate),
        where("plannedDate", "<=", endDate),
        orderBy("plannedDate", "asc"),
      );
      const snapshot = await getDocs(q);

      const sessions = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: toJsDate(data.createdAt),
          updatedAt: toJsDate(data.updatedAt),
        } as Session & { id: string };
      });

      // Populate coach display info for coach-issued sessions so the UI can
      // show the coach's name (e.g. "Set by <Coach Name>") rather than a
      // generic fallback.
      const sessionsWithCoachDetails = await Promise.all(
        sessions.map(async (session) => {
          try {
            if (session.coachID) {
              const coachInfo = await getCoachDisplayInfo(session.coachID);
              if (coachInfo) {
                return { ...session, coach: coachInfo };
              }
            }
            return session;
          } catch (error) {
            console.error("Error fetching coach details for session:", error);
            return session;
          }
        }),
      );

      return sessionsWithCoachDetails;
    } catch (error) {
      console.error("Error getting sessions for date range:", error);
      throw new Error("Failed to get sessions for date range");
    }
  }

  /**
   * Get sessions for a specific date
   */
  static async getSessionsForDate(
    userId: string,
    date: string,
  ): Promise<(Session & { id: string })[]> {
    if (!userId) {
      throw new Error("User ID is required");
    }

    if (!date) {
      throw new Error("Date is required");
    }

    try {
      const sessionsRef = collection(db, "sessions", userId, "sessions");
      const q = query(sessionsRef, where("plannedDate", "==", date));
      const snapshot = await getDocs(q);

      return snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: toJsDate(data.createdAt),
          updatedAt: toJsDate(data.updatedAt),
        } as Session & { id: string };
      });
    } catch (error) {
      console.error("Error getting sessions for date:", error);
      throw new Error("Failed to get sessions for date");
    }
  }

  /**
   * Update a session
   */
  static async updateSession(
    userId: string,
    sessionId: string,
    updates: Partial<Omit<Session, "id" | "createdAt" | "updatedAt">>,
  ): Promise<void> {
    if (!userId) {
      throw new Error("User ID is required");
    }

    if (!sessionId) {
      throw new Error("Session ID is required");
    }

    try {
      let programToPatch: {
        session: Session;
        newPlannedDate: string;
      } | null = null;

      // If planned date is being updated, we need to handle day post changes
      if (updates.plannedDate) {
        // First, get the current session to find the old planned date
        const currentSession = await this.getSession(userId, sessionId);
        if (!currentSession) {
          throw new Error("Session not found");
        }

        const oldPlannedDate = currentSession.plannedDate;
        const newPlannedDate = updates.plannedDate;

        // If the date is actually changing, update day posts
        if (oldPlannedDate !== newPlannedDate) {
          const oldDateID = oldPlannedDate.replace(/-/g, ""); // Convert YYYY-MM-DD to YYYYMMDD
          const newDateID = newPlannedDate.replace(/-/g, ""); // Convert YYYY-MM-DD to YYYYMMDD

          // Remove session ID from old day post and add to new day post
          await Promise.all([
            removeLogIDFromDayPost(userId, oldDateID, sessionId, "sessionIDs"),
            addLogIDToDayPost(userId, newDateID, sessionId, "sessionIDs"),
          ]);

          if (currentSession.programLinkage?.programId) {
            programToPatch = { session: currentSession, newPlannedDate };
          }
        }
      }

      // Update the session document
      const docRef = doc(db, "sessions", userId, "sessions", sessionId);
      // Sanitize updates deeply to avoid undefined anywhere in the payload
      const cleaned = sanitizeForFirestore({
        ...updates,
        updatedAt: Timestamp.now(),
      });
      await updateDoc(docRef, cleaned);

      // Keep the program's copy of the schedule in step with the session
      // (plan decision 3). Done after the session write so a missing program
      // never blocks the move.
      if (programToPatch) {
        await this.patchProgramPlannedDate(
          userId,
          sessionId,
          programToPatch.session,
          programToPatch.newPlannedDate,
        );
      }
    } catch (error) {
      console.error("Error updating session:", error);
      throw new Error("Failed to update session");
    }
  }

  /**
   * Update `linkedSessions[].plannedDate` for `sessionId` on the program the
   * session belongs to. Coach-issued sessions live under
   * `organisations/{organisationID}/programs/{programId}`; self-made programs
   * under `programs/{uid}/programs/{programId}`. Skips silently when the
   * program cannot be located or does not reference the session, and never
   * throws: the session move itself has already succeeded.
   */
  private static async patchProgramPlannedDate(
    userId: string,
    sessionId: string,
    session: Session,
    newPlannedDate: string,
  ): Promise<void> {
    const programId = session.programLinkage?.programId;
    if (!programId) return;

    const isCoachProgram = Boolean(
      session.coachIssued || session.coachID || session.organisationID,
    );
    const programRef = isCoachProgram
      ? session.organisationID
        ? doc(db, "organisations", session.organisationID, "programs", programId)
        : null
      : doc(db, "programs", userId, "programs", programId);
    if (!programRef) return;

    try {
      const snap = await getDoc(programRef);
      if (!snap.exists()) return;
      const linkedSessions = (snap.data()?.linkedSessions ?? []) as {
        sessionId: string;
        plannedDate: string;
      }[];
      if (!Array.isArray(linkedSessions)) return;
      if (!linkedSessions.some((ref) => ref?.sessionId === sessionId)) return;

      const next = linkedSessions.map((ref) =>
        ref?.sessionId === sessionId
          ? { ...ref, plannedDate: newPlannedDate }
          : ref,
      );
      await updateDoc(programRef, {
        linkedSessions: next,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.warn(
        `Could not update program ${programId} plannedDate for session ${sessionId}:`,
        error,
      );
    }
  }

  /**
   * Delete a session.
   *
   * One batch: unlink it from its training log (array + legacy scalar), remove
   * it from `dayPosts.sessionIDs`, then delete the document. The training log
   * itself is never deleted (plan decision 5).
   */
  static async deleteSession(userId: string, sessionId: string): Promise<void> {
    if (!userId) {
      throw new Error("User ID is required");
    }

    if (!sessionId) {
      throw new Error("Session ID is required");
    }

    try {
      const session = await this.getSession(userId, sessionId);
      const batch = writeBatch(db);

      if (session?.linkedTrainingLogID) {
        await this.addLogUnlinkToBatch(
          batch,
          userId,
          sessionId,
          session.linkedTrainingLogID,
        );
      }

      if (session?.plannedDate) {
        const dateID = session.plannedDate.replace(/-/g, ""); // YYYY-MM-DD -> YYYYMMDD
        const dayPostRef = doc(db, "dayPosts", userId, "posts", dateID);
        const dayPostSnap = await getDoc(dayPostRef);
        if (dayPostSnap.exists()) {
          batch.update(dayPostRef, { sessionIDs: arrayRemove(sessionId) });
        }
      }

      batch.delete(doc(db, "sessions", userId, "sessions", sessionId));
      await batch.commit();
    } catch (error) {
      console.error("Error deleting session:", error);
      throw new Error("Failed to delete session");
    }
  }

  // ===== TRAINING LOG <-> SESSION LINKS =====
  // Write rules (plan section 2): the log's `linkedSessionIDs` array is the
  // source of truth, the legacy `linkedSessionID` scalar always equals element
  // 0 (or is deleted when the array is empty), and the session's
  // `linkedTrainingLogID` stays scalar. Every write goes through one batch and
  // never passes `undefined` to Firestore.

  private static trainingLogRef(userId: string, logId: string) {
    return doc(db, "trainingLogs", userId, "logs", logId);
  }

  /**
   * Link session `sessionId` to training log `logId`.
   *
   * - log: `linkedSessionIDs` gains the id (arrayUnion), `linkedSessionID` is
   *   set to element 0 of the resulting array.
   * - session: `linkedTrainingLogID = logId`, `status = "complete"`.
   * - `dateID` is the log's day (YYYYMMDD). When the session is planned for
   *   that same day and the day post exists, the id is also ensured in
   *   `dayPosts.sessionIDs` (coach-issued sessions written by PeakElite can
   *   be missing from it).
   *
   * Linking a session that already points at another log removes it from that
   * log in the same batch. Linking it to the log it is already on only
   * re-writes this log.
   */
  static async linkSessionToLog(
    userId: string,
    sessionId: string,
    logId: string,
    dateID: string,
  ): Promise<void> {
    if (!userId) throw new Error("User ID is required");
    if (!sessionId) throw new Error("Session ID is required");
    if (!logId) throw new Error("Training log ID is required");

    try {
      const logRef = this.trainingLogRef(userId, logId);
      const sessionRef = doc(db, "sessions", userId, "sessions", sessionId);
      const [logSnap, sessionSnap] = await Promise.all([
        getDoc(logRef),
        getDoc(sessionRef),
      ]);
      if (!logSnap.exists()) {
        throw new Error(`Training log ${logId} not found`);
      }
      if (!sessionSnap.exists()) {
        throw new Error(`Session ${sessionId} not found`);
      }

      const log = logSnap.data() as TrainingLog;
      const session = sessionSnap.data() as Session;
      const current = getLinkedSessionIDs(log);
      const next = current.includes(sessionId)
        ? current
        : [...current, sessionId];

      const batch = writeBatch(db);
      // A session belongs to one log. Drop it from the previous log in this
      // same batch so a later "delete them" on the old log cannot remove a
      // session that now belongs to this one.
      const previousLogId = session.linkedTrainingLogID;
      if (previousLogId && previousLogId !== logId) {
        await this.addLogUnlinkToBatch(
          batch,
          userId,
          sessionId,
          previousLogId,
        );
      }
      batch.update(logRef, {
        // Legacy docs carry only the scalar: write the full array once so the
        // old id is not lost; otherwise arrayUnion is enough.
        linkedSessionIDs: Array.isArray(log.linkedSessionIDs)
          ? arrayUnion(sessionId)
          : next,
        linkedSessionID: next[0],
      });
      batch.update(sessionRef, {
        linkedTrainingLogID: logId,
        status: "complete",
        updatedAt: Timestamp.now(),
      });

      const sessionDateID = (session.plannedDate || "").replace(/-/g, "");
      if (dateID && sessionDateID === dateID) {
        const dayPostRef = doc(db, "dayPosts", userId, "posts", dateID);
        const dayPostSnap = await getDoc(dayPostRef);
        if (dayPostSnap.exists()) {
          batch.update(dayPostRef, { sessionIDs: arrayUnion(sessionId) });
        }
      }

      await batch.commit();
    } catch (error) {
      console.error("Error linking session to training log:", error);
      throw error;
    }
  }

  /**
   * Unlink session `sessionId` from training log `logId`.
   *
   * - log: `linkedSessionIDs` loses the id (arrayRemove); `linkedSessionID`
   *   becomes the next remaining id or is deleted with `deleteField()`.
   * - session: `linkedTrainingLogID` deleted with `deleteField()`.
   *
   * The session stays `complete` and reappears under "Sessions to log".
   */
  static async unlinkSessionFromLog(
    userId: string,
    sessionId: string,
    logId: string,
  ): Promise<void> {
    if (!userId) throw new Error("User ID is required");
    if (!sessionId) throw new Error("Session ID is required");
    if (!logId) throw new Error("Training log ID is required");

    try {
      const batch = writeBatch(db);
      await this.addLogUnlinkToBatch(batch, userId, sessionId, logId);
      batch.update(doc(db, "sessions", userId, "sessions", sessionId), {
        linkedTrainingLogID: deleteField(),
        updatedAt: Timestamp.now(),
      });
      await batch.commit();
    } catch (error) {
      console.error("Error unlinking session from training log:", error);
      throw error;
    }
  }

  /**
   * Queue the log-side half of an unlink on `batch`. Skips silently when the
   * log no longer exists so a session can always be detached or deleted.
   */
  private static async addLogUnlinkToBatch(
    batch: WriteBatch,
    userId: string,
    sessionId: string,
    logId: string,
  ): Promise<void> {
    const logRef = this.trainingLogRef(userId, logId);
    const logSnap = await getDoc(logRef);
    if (!logSnap.exists()) return;

    const log = logSnap.data() as TrainingLog;
    const remaining = getLinkedSessionIDs(log).filter(
      (id) => id !== sessionId,
    );
    batch.update(logRef, {
      linkedSessionIDs: Array.isArray(log.linkedSessionIDs)
        ? arrayRemove(sessionId)
        : remaining,
      linkedSessionID: remaining.length > 0 ? remaining[0] : deleteField(),
    });
  }

  /**
   * Start a session (change status to executing)
   */
  static async startSession(userId: string, sessionId: string): Promise<void> {
    try {
      await this.updateSession(userId, sessionId, {
        status: "executing",
        startTime: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Error starting session:", error);
      throw error;
    }
  }

  /**
   * Was this session created by pressing Start (ad-hoc) rather than
   * scheduled beforehand? Only a persisted `startedAdHoc === true` counts.
   * Planned sessions, including ones started immediately, are not ad-hoc:
   * cancelling them resets the attempt instead of deleting the document.
   */
  static isAdHocSession(session: Pick<Session, "startedAdHoc">): boolean {
    return session.startedAdHoc === true;
  }

  /**
   * Cancel an in-progress attempt without completing it: back to `planned`,
   * start/end cleared, so it reappears on the schedule. `measurements`, when
   * given, restores the pre-start activity list; otherwise the attempt's
   * `timeLogged` stamps are cleared so nothing counts as done.
   */
  static async cancelSessionExecution(
    userId: string,
    sessionId: string,
    measurements?: ActivityMeasurement[],
  ): Promise<void> {
    if (!userId) throw new Error("User ID is required");
    if (!sessionId) throw new Error("Session ID is required");
    try {
      const updates: Record<string, any> = {
        status: "planned",
        startTime: deleteField(),
        endTime: deleteField(),
        updatedAt: Timestamp.now(),
      };
      if (measurements) {
        updates.ActivityMeasurementList = sanitizeForFirestore(measurements);
      } else {
        const current = await this.getSession(userId, sessionId);
        if (current?.ActivityMeasurementList) {
          updates.ActivityMeasurementList = sanitizeForFirestore(
            current.ActivityMeasurementList.map((m) => {
              const { timeLogged: _dropped, ...rest } = m;
              return rest;
            }),
          );
        }
      }
      await updateDoc(doc(db, "sessions", userId, "sessions", sessionId), updates);
    } catch (error) {
      console.error("Error cancelling session:", error);
      throw error;
    }
  }

  /**
   * Complete a session (change status to complete)
   */
  static async completeSession(
    userId: string,
    sessionId: string,
  ): Promise<void> {
    try {
      await this.updateSession(userId, sessionId, {
        status: "complete",
        endTime: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Error completing session:", error);
      throw error;
    }
  }

  /**
   * Mark an activity measurement as logged (set timeLogged)
   */
  static async markActivityComplete(
    userId: string,
    sessionId: string,
    measurementIndex: number,
  ): Promise<void> {
    try {
      const session = await this.getSession(userId, sessionId);
      if (!session) {
        throw new Error("Session not found");
      }

      const measurements = session.ActivityMeasurementList || [];
      if (measurementIndex >= 0 && measurementIndex < measurements.length) {
        const updatedMeasurements = [...measurements];
        updatedMeasurements[measurementIndex] = {
          ...updatedMeasurements[measurementIndex],
          timeLogged: new Date().toISOString(),
        };

        await this.updateSession(userId, sessionId, {
          ActivityMeasurementList: updatedMeasurements,
        });
      } else {
        throw new Error("Invalid measurement index");
      }
    } catch (error) {
      console.error("Error marking activity complete:", error);
      throw error;
    }
  }

  /**
   * Update a planned activity measurement and mark it as logged (set timeLogged)
   */
  static async addActivityMeasurement(
    userId: string,
    sessionId: string,
    measurementIndex: number,
    updatedVariables: ActivityVariable[],
  ): Promise<void> {
    try {
      const session = await this.getSession(userId, sessionId);
      if (!session) {
        throw new Error("Session not found");
      }

      const measurements = session.ActivityMeasurementList || [];

      // Validate measurement index
      if (measurementIndex < 0 || measurementIndex >= measurements.length) {
        throw new Error("Invalid measurement index");
      }

      // Update the measurement with new variables and set timeLogged
      const updatedMeasurements = [...measurements];
      updatedMeasurements[measurementIndex] = {
        ...updatedMeasurements[measurementIndex],
        variables: updatedVariables,
        timeLogged: new Date().toISOString(),
      };

      // Update the session
      await this.updateSession(userId, sessionId, {
        ActivityMeasurementList: updatedMeasurements,
      });
    } catch (error) {
      console.error("Error adding activity measurement:", error);
      throw error;
    }
  }

  /**
   * Update an activity measurement in a session
   */
  static async updateActivityMeasurement(
    userId: string,
    sessionId: string,
    measurementIndex: number,
    updates: Partial<ActivityMeasurement>,
  ): Promise<void> {
    try {
      const session = await this.getSession(userId, sessionId);
      if (!session) {
        throw new Error("Session not found");
      }

      const measurements = session.ActivityMeasurementList || [];
      if (measurementIndex < 0 || measurementIndex >= measurements.length) {
        throw new Error("Invalid measurement index");
      }

      // Build a cleaned version of the updated measurement to avoid any `undefined` values
      const current = measurements[measurementIndex];
      const hasTimeLoggedKey = Object.prototype.hasOwnProperty.call(
        updates,
        "timeLogged",
      );

      // Merge variables, normalizing undefined → null for numeric measurements
      const mergedVariables =
        updates.variables !== undefined
          ? (updates.variables || []).map((v) => ({
              ...v,
              measurement:
                (v as any).measurement === undefined
                  ? null
                  : (v as any).measurement,
            }))
          : current.variables;

      // Start with base merged object
      const merged: ActivityMeasurement = {
        ...current,
        ...(updates as ActivityMeasurement),
        variables: mergedVariables,
      };

      // If caller explicitly tried to clear timeLogged (set undefined), remove the key
      if (hasTimeLoggedKey && updates.timeLogged === undefined) {
        delete (merged as any).timeLogged;
      }

      // Final guard: remove any remaining undefined keys from the merged object
      const cleaned: any = {};
      Object.keys(merged).forEach((k) => {
        const val = (merged as any)[k];
        if (val !== undefined) {
          cleaned[k] = val;
        }
      });

      const updatedMeasurements = [...measurements];
      updatedMeasurements[measurementIndex] = cleaned as ActivityMeasurement;

      // Update the session
      const cleanedList = sanitizeForFirestore(updatedMeasurements);
      await this.updateSession(userId, sessionId, {
        ActivityMeasurementList: cleanedList,
      });
    } catch (error) {
      console.error("Error updating activity measurement:", error);
      throw error;
    }
  }

  /**
   * Delete an activity measurement from a session
   */
  static async deleteActivityMeasurement(
    userId: string,
    sessionId: string,
    measurementIndex: number,
  ): Promise<void> {
    try {
      const session = await this.getSession(userId, sessionId);
      if (!session) {
        throw new Error("Session not found");
      }

      const measurements = session.ActivityMeasurementList || [];
      if (measurementIndex < 0 || measurementIndex >= measurements.length) {
        throw new Error("Invalid measurement index");
      }

      // Remove the measurement
      const updatedMeasurements = measurements.filter(
        (_, index) => index !== measurementIndex,
      );

      // Update the session
      await this.updateSession(userId, sessionId, {
        ActivityMeasurementList: updatedMeasurements,
      });
    } catch (error) {
      console.error("Error deleting activity measurement:", error);
      throw error;
    }
  }

  /**
   * Add a freeform activity measurement to a session (not from planned activities)
   */
  static async addFreeformMeasurement(
    userId: string,
    sessionId: string,
    measurement: Omit<ActivityMeasurement, "timeLogged" | "activityIndex">,
  ): Promise<void> {
    try {
      const session = await this.getSession(userId, sessionId);
      if (!session) {
        throw new Error("Session not found");
      }

      // Create the complete measurement
      const completeMeasurement: ActivityMeasurement = {
        ...measurement,
        timeLogged: new Date().toISOString(),
        // Note: activityIndex is not set for freeform measurements
      };

      // Add the measurement to the session
      const currentMeasurements = session.ActivityMeasurementList || [];
      const updatedMeasurements = [...currentMeasurements, completeMeasurement];

      // Update the session with the new measurement
      await this.updateSession(userId, sessionId, {
        ActivityMeasurementList: updatedMeasurements,
      });

      // console.log("Freeform measurement added successfully");
    } catch (error) {
      console.error("Error adding freeform measurement:", error);
      throw error;
    }
  }

  /**
   * Link a training log to a session (session side only).
   * @deprecated Use `linkSessionToLog`, which also writes the log's
   * `linkedSessionIDs` / `linkedSessionID` in the same batch.
   */
  static async linkTrainingLog(
    userId: string,
    sessionId: string,
    trainingLogId: string,
  ): Promise<void> {
    try {
      await this.updateSession(userId, sessionId, {
        linkedTrainingLogID: trainingLogId,
      });
      // console.log(
      //   `Successfully linked training log ${trainingLogId} to session ${sessionId}`,
      // );
    } catch (error) {
      console.error("Error linking training log to session:", error);
      throw error;
    }
  }

  // ===== SESSION TEMPLATE METHODS =====

  /**
   * Create a new session template
   */
  static async createSessionTemplate(
    userId: string,
    templateData: {
      name?: string;
      description?: string;
      ActivityMeasurementList?: ActivityMeasurement[];
      trainingTypeID?: string;
      /** @deprecated Legacy composite ID kept for old callers; use trainingTypeID */
      sessionTypeID?: string;
      periodisation?: string;
    },
  ): Promise<string> {
    if (!userId) {
      throw new Error("User ID is required");
    }

    try {
      const template: Omit<SessionTemplate, "id"> = {
        name: templateData.name?.trim() || "",
        description: templateData.description?.trim() || "",
        ActivityMeasurementList: templateData.ActivityMeasurementList || [],
        ...(typeof templateData.trainingTypeID === "string" &&
        templateData.trainingTypeID.trim() !== ""
          ? { trainingTypeID: templateData.trainingTypeID.trim() }
          : {}),
        ...(templateData.sessionTypeID &&
        typeof templateData.sessionTypeID === "string" &&
        templateData.sessionTypeID.trim() !== ""
          ? { sessionTypeID: templateData.sessionTypeID.trim() }
          : {}),
        ...(templateData.periodisation &&
        typeof templateData.periodisation === "string" &&
        templateData.periodisation.trim() !== ""
          ? { periodisation: templateData.periodisation.trim() as any }
          : {}),
      };

      const docRef = await addDoc(
        collection(db, "sessionTemplates", userId, "templates"),
        template,
      );

      return docRef.id;
    } catch (error) {
      console.error("Error creating session template:", error);
      throw new Error("Failed to create session template");
    }
  }

  /**
   * Get a session template by ID
   */
  static async getSessionTemplate(
    userId: string,
    templateId: string,
  ): Promise<SessionTemplate | null> {
    if (!userId) {
      throw new Error("User ID is required");
    }

    if (!templateId) {
      throw new Error("Template ID is required");
    }

    try {
      const docRef = doc(
        db,
        "sessionTemplates",
        userId,
        "templates",
        templateId,
      );
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
        } as SessionTemplate;
      }

      return null;
    } catch (error) {
      console.error("Error getting session template:", error);
      throw new Error("Failed to get session template");
    }
  }

  /**
   * Get all session templates for a user
   */
  static async getAllSessionTemplates(
    userId: string,
  ): Promise<(SessionTemplate & { id: string })[]> {
    if (!userId) {
      throw new Error("User ID is required");
    }

    try {
      const templatesRef = collection(
        db,
        "sessionTemplates",
        userId,
        "templates",
      );
      const q = query(templatesRef, orderBy("name", "asc"));
      const snapshot = await getDocs(q);

      return snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
        } as SessionTemplate & { id: string };
      });
    } catch (error) {
      console.error("Error getting all session templates:", error);
      throw new Error("Failed to get session templates");
    }
  }

  /**
   * Update a session template
   */
  static async updateSessionTemplate(
    userId: string,
    templateId: string,
    updates: Partial<Omit<SessionTemplate, "id">>,
  ): Promise<void> {
    if (!userId) {
      throw new Error("User ID is required");
    }

    if (!templateId) {
      throw new Error("Template ID is required");
    }

    try {
      // Filter out undefined values - Firestore doesn't accept undefined
      const cleanUpdates: any = {};
      Object.keys(updates).forEach((key) => {
        const value = updates[key as keyof typeof updates];
        if (value !== undefined) {
          cleanUpdates[key] = value;
        }
      });

      if (Object.keys(cleanUpdates).length === 0) {
        throw new Error("No valid updates provided");
      }

      const docRef = doc(
        db,
        "sessionTemplates",
        userId,
        "templates",
        templateId,
      );
      await updateDoc(docRef, cleanUpdates);
    } catch (error) {
      console.error("Error updating session template:", error);
      throw new Error("Failed to update session template");
    }
  }

  /**
   * Delete a session template
   */
  static async deleteSessionTemplate(
    userId: string,
    templateId: string,
  ): Promise<void> {
    if (!userId) {
      throw new Error("User ID is required");
    }

    if (!templateId) {
      throw new Error("Template ID is required");
    }

    try {
      const docRef = doc(
        db,
        "sessionTemplates",
        userId,
        "templates",
        templateId,
      );
      await deleteDoc(docRef);
    } catch (error) {
      console.error("Error deleting session template:", error);
      throw new Error("Failed to delete session template");
    }
  }
}

// Export singleton instance for convenience
export const sessionService = SessionService;
